'use client';

import { useState, useEffect, useMemo } from 'react';
import { fabricThumbnailUrl } from '@/lib/images/fabricThumbnail';
import { createPortal } from 'react-dom';
import { FaTimes, FaSearchPlus } from 'react-icons/fa';

interface Fabric {
  fabric_id: number;
  fabric_code: string;
  fabric_name: string;
  fabric_type: string;
  opacity: string;
  fabric_image_url: string;
}

interface FabricGridProps {
  fabricsByOpacity: Record<string, Fabric[]>;
  totalCount: number;
}

export default function FabricGrid({ fabricsByOpacity, totalCount }: FabricGridProps) {
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  // Flatten all fabrics into a single array for prev/next navigation
  const allFabrics = useMemo(() => {
    const flat: Fabric[] = [];
    Object.values(fabricsByOpacity).forEach((fabrics) => flat.push(...fabrics));
    return flat;
  }, [fabricsByOpacity]);

  const currentFabric = zoomIndex !== null ? allFabrics[zoomIndex] : null;

  useEffect(() => { setMounted(true); }, []);

  // Keyboard navigation + scroll lock
  useEffect(() => {
    if (zoomIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomIndex(null);
      if (e.key === 'ArrowLeft') setZoomIndex((prev) => prev !== null ? (prev - 1 + allFabrics.length) % allFabrics.length : null);
      if (e.key === 'ArrowRight') setZoomIndex((prev) => prev !== null ? (prev + 1) % allFabrics.length : null);
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [zoomIndex, allFabrics.length]);

  if (totalCount === 0) return null;

  // Find the flat index for a fabric
  const getFlatIndex = (fabric: Fabric) => allFabrics.findIndex((f) => f.fabric_id === fabric.fabric_id);

  const zoomModal = currentFabric && mounted ? createPortal(
    <div
      className="fixed inset-0 bg-black/95 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={() => setZoomIndex(null)}
      role="dialog" aria-modal="true" aria-label="Fabric photo preview"
    >
      {/* Close */}
      <button
        aria-label="Close fabric preview"
        className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
        onClick={() => setZoomIndex(null)}
      >
        <FaTimes className="w-6 h-6" />
      </button>

      {/* Prev */}
      <button
        aria-label="Previous fabric"
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
        onClick={(e) => { e.stopPropagation(); setZoomIndex((prev) => prev !== null ? (prev - 1 + allFabrics.length) % allFabrics.length : null); }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
      </button>

      {/* Next */}
      <button
        aria-label="Next fabric"
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3"
        onClick={(e) => { e.stopPropagation(); setZoomIndex((prev) => prev !== null ? (prev + 1) % allFabrics.length : null); }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>

      {/* Fabric Image */}
      <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={currentFabric.fabric_image_url}
          alt={`Fabric ${currentFabric.fabric_code}`}
          className="w-full max-h-[70vh] object-contain rounded-lg"
        />
        <div className="text-center mt-4">
          <p className="text-white text-lg font-semibold">{currentFabric.fabric_code}</p>
          <p className="text-white/70 text-sm capitalize">
            {currentFabric.fabric_type} &middot; {currentFabric.opacity.replace('-', ' ')}
          </p>
        </div>
      </div>

      {/* Counter */}
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
        {zoomIndex !== null ? zoomIndex + 1 : 0} / {allFabrics.length}
      </p>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="mt-8 pt-6 border-t">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Available Fabrics</h2>
        <p className="text-sm text-gray-500 mb-4">{totalCount} fabric options available — click to zoom, use arrows to browse</p>

        {Object.entries(fabricsByOpacity).map(([opacity, opacityFabrics]) => (
          <div key={opacity} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 capitalize flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${opacity === 'blackout' ? 'bg-gray-900' : opacity === 'light-filtering' ? 'bg-yellow-400' : 'bg-blue-400'}`} />
              {opacity.replace('-', ' ')} ({opacityFabrics.length})
            </h3>
            <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
              {opacityFabrics.map((fabric) => (
                <button
                  key={fabric.fabric_id}
                  onClick={() => setZoomIndex(getFlatIndex(fabric))}
                  className="group text-left"
                  title={`${fabric.fabric_code} - ${opacity.replace('-', ' ')}`}
                >
                  <div className="aspect-square rounded-md overflow-hidden border-2 border-gray-200 hover:border-primary-red transition-colors relative">
                    <img
                      src={fabricThumbnailUrl(fabric.fabric_image_url)}
                      width={128}
                      height={128}
                      decoding="async"
                      alt={`Fabric ${fabric.fabric_code}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <FaSearchPlus className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 text-center mt-1 truncate group-hover:text-primary-red">
                    {fabric.fabric_code}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {zoomModal}
    </>
  );
}
